/**
 * Per-distributor sub-region inspect view per scope §6.1.1. Atlas Day 2 P6
 * worked example demonstrating the InspectShell + LayerSection + L10
 * ComposabilityReservedLayer + classification-map-derived L5/L6/L9 +
 * curated propagation map L8 + per-region L4/L7/L11 pattern.
 *
 * The remaining 7 sub-region inspect views (Per-supplier, Per-brand,
 * Per-manufacturer, Per-category, Business defaults full inspect,
 * Path mechanics, Weight components) land in Atlas Day 2.5 per the
 * close-out plan, using this component as the canonical pattern reference.
 *
 * Substrate per Phase 0 #1 + Day 1 D3: 1 active distributor (BLVS).
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { PER_DISTRIBUTOR_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PerDistributorInspect(): JSX.Element {
  const distFields = FIELD_CLASSIFICATIONS.distributors;
  const fieldEntries = Object.entries(distFields);

  // L5 split per scope §6.1.1: section-by-section per nature.
  const strategicFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'strategic');
  const mechanicalFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'mechanical');

  // L6 split per scope §6.1.1: per-field authority altitude where present.
  const ownershipFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'ownership');
  const operationalFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'operational');
  const naLayer6Fields = fieldEntries.filter(([, c]) => c.layer6Authority === undefined);

  return (
    <InspectShell
      clusterName="Per-entity configuration"
      subRegionName="Per-distributor"
      surfaceLine="ISG works with N distributors; K active. Credit posture and per-distributor overrides for params + cost lines. → Credit gate"
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per-distributor field split per scope §6.1.1: <code>credit_limit</code> +{' '}
          <code>payment_terms</code> = <strong>judgment</strong> (ISG&apos;s choices);{' '}
          <code>lead_time_days</code> = <strong>mechanical</strong> (distributor-reported, not on
          distributors substrate today — supplier-side only); <code>is_active</code> ={' '}
          <strong>judgment</strong> (operating choice); <code>tax_exempt</code> ={' '}
          <strong>mechanical</strong> (regulatory).
        </p>
        <p>
          <code>estimated_tax_pct</code> = ambiguous (chat-ambiguity item per Sol D6 lock;
          mechanical regional fact vs strategic ISG-set estimate);{' '}
          <code>marketplace_permissions</code> = <strong>judgment</strong> (cross-domain
          permission stance).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Strategic section ({strategicFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {strategicFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split('.')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Mechanical section ({mechanicalFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {mechanicalFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split('.')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Ownership-altitude ({ownershipFields.length} fields) — define structural stance per
            distributor
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {ownershipFields.map(([field]) => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Operational-altitude ({operationalFields.length} fields) — routine operating
            tuning
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {operationalFields.map(([field]) => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
        {naLayer6Fields.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Region-altitude ({naLayer6Fields.length} fields, layer 6 N/A per three-state
              applicability)
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {naLayer6Fields.map(([field, c]) => (
                <li key={field}>
                  <code>{field}</code>
                  {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Three-pattern per scope §6.1.1 L7 + Phase 0 verifications:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <strong>Provenance-tracked fields</strong> (credit_limit, payment_terms, is_active,
            tax_exempt): backfill-traced or fully-traced per <code>distributors.provenance</code>{' '}
            sidecar (Day 67 P2.2 substrate). Per-row change history available.
          </li>
          <li>
            <strong>Non-tracked fields</strong> (name, code, email, marketplace_permissions —
            depending on substrate state): untraced today; field-level changes don&apos;t
            propagate to provenance sidecar.
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker
          convention):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PER_DISTRIBUTOR_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Reversible-with-residue per scope §6.1.1 L9. Reversible going forward; credit ledger
          entries preserve prior credit-limit state as documented at the moment of credit
          allocation. Per-field reversibility classification:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Cluster-contribution pattern per scope §6.1.1 L11. Per-distributor configuration
          contributes to ISG&apos;s position via the cluster&apos;s role: structural posture for
          who ISG buys through. Specific per-distributor configurations contribute working-
          capital allocation and credit posture surfaced in title block sentence 2 (&quot;ISG
          buys primarily through {`{distributor.name}`} on net-{`{payment_terms}`} terms&quot;).
        </p>
      </LayerSection>
    </InspectShell>
  );
}
